import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Table, Button, Input } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UpCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import FileLoaderService, { statuses } from '../services/file-loader-service';
import startCase from 'lodash.startcase';
import { fileStatusChange, fileTriggered } from '../utils/events';
import { formatDateShort } from '../utils/format';
import ErrorsTooltip from './shared/errors-tooltip';

const DEFUALT_PAGE_SIZE = 50;

const ColumnFilter = ( { name, filterText, onFilterTextChange, setSelectedKeys, confirm } ) => {
    return (
        <div style={ { padding: '12px', width: '300px' } }>
            <Input
                placeholder="Search"
                name='filterText'
                value={ filterText[ name ] || '' }
                onChange={ ( e ) => onFilterTextChange( name, e.target.value ) }
                onPressEnter={ () => {
                    const value = filterText[ name ] || '';
                    setSelectedKeys( value ? [ value ] : [] );
                    confirm();
                } }
            />
            <div style={ { marginTop: '12px', display: 'flex', justifyContent: 'flex-end' } }>
                <Button
                    style={ { marginLeft: '8px' } }
                    onClick={ () => {
                        onFilterTextChange( name, '' );
                        setSelectedKeys( [] );
                        confirm();
                    } }
                >
                    Clear
                </Button>
                <Button
                    type="primary"
                    style={ { marginLeft: '8px' } }
                    onClick={ () => {
                        const value = filterText[ name ] || '';
                        setSelectedKeys( value ? [ value ] : [] );
                        confirm();
                    } }
                >
                    Apply
                </Button>
            </div>
        </div>
    );
};

const FileRecordTable = ( props ) => {
    const {
        fileId,
        pageSize = DEFUALT_PAGE_SIZE,
        auth,
        className,
        onFileRecordsLoaded,
        showDropDown,
        additionalColumns,
        getColumnOrder,
        shouldShowColumn,
        getColumnTitle,
        renderValue,
        getColumnType
    } = props;

    const [ file, setFile ] = useState( null );
    const [ page, setPage ] = useState( 1 );
    const [ records, setRecords ] = useState( null );
    const [ loading, setLoading ] = useState( false );
    const [ filters, setFilters ] = useState( [] );
    const [ sorter, setSorter ] = useState( { field: 'fileRecordStatus', order: 'ascend' } );
    const [ filterText, setFilterText ] = useState( {} );

    const loadFileRecords = useCallback( async ( fileId, page, filters, sorter ) => {
        if ( !fileId ) {
            setRecords( [] );
            return;
        }

        const searchFilters = {};
        Object.keys( filters ).forEach( key => {
            if ( filters[ key ] && filters[ key ].length > 0 ) {
                searchFilters[ key ] = filters[ key ][ 0 ];
            }
        } );

        let searchSort = undefined;
        if ( sorter && sorter.field ) {
            searchSort = [ {
                name: sorter.field,
                direction: sorter.order === 'ascend' ? 'ASC' : 'DESC',
                type: sorter.column?.type || 'string'
            } ];
        }

        setLoading( true );
        try {
            const fileLoader = FileLoaderService( auth.state );
            const result = await fileLoader.searchRecords(
                fileId,
                pageSize,
                ( ( Math.max( 1, page ) - 1 ) * pageSize ),
                searchFilters,
                searchSort
            );
            const { fileRecords, limit, offset, cursor, ...file } = result;

            setFile( file );
            setRecords( fileRecords );

            if ( typeof onFileRecordsLoaded === 'function' ) {
                onFileRecordsLoaded( file, result );
            }
        } catch ( error ) {
            console.error( 'Error fetching file records', error.status );
            setFile( null );
            setRecords( null );
        } finally {
            setLoading( false );
        }
    }, [ auth.state, pageSize, onFileRecordsLoaded ] );

    const handleTableChange = useCallback( ( pagination, filters, sorter ) => {
        setPage( pagination.current );
        setFilters( filters );
        setSorter( sorter );
        loadFileRecords( fileId, pagination.current, filters, sorter );
    }, [ fileId, loadFileRecords ] );

    useEffect( () => {
        loadFileRecords( fileId, page, filters, sorter );
    }, [ fileId, pageSize, auth?.state?.claims?.company_id ] );

    useEffect( () => {
        const handleFileStatusChange = ( id ) => {
            if ( id === fileId ) {
                loadFileRecords( id, page, filters, sorter );
            }
        };

        const handleFileTriggered = ( id ) => {
            setTimeout( () => handleFileStatusChange( id ), 5000 );
        };

        fileStatusChange.on( handleFileStatusChange );
        fileTriggered.on( handleFileTriggered );

        return () => {
            fileStatusChange.off( handleFileStatusChange );
            fileTriggered.off( handleFileTriggered );
        };
    }, [ fileId, page, filters, sorter, loadFileRecords ] );

    const columns = useMemo( () => {
        if ( !file || !records || records.length === 0 ) {
            return [];
        }

        const getColumnFilterProps = ( name, showDropDown ) => ( {
            filterDropdown: !showDropDown ? false : ( { setSelectedKeys, confirm } ) => (
                <ColumnFilter
                    name={ name }
                    filterText={ filterText }
                    onFilterTextChange={ ( name, value ) => setFilterText( prev => ( { ...prev, [ name ]: value } ) ) }
                    setSelectedKeys={ setSelectedKeys }
                    confirm={ confirm }
                />
            )
        } );

        const columnMap = {
            fileRecordStatus: {
                id: 'fileRecordStatus',
                title: '',
                key: 'fileRecordStatus',
                dataIndex: 'fileRecordStatus',
                sorter: true,
                sortOrder: sorter?.field === 'fileRecordStatus' ? sorter.order : null,
                render: ( fileRecordStatus, { _errors } ) => {
                    if ( fileRecordStatus === statuses.LOADED ) {
                        return <CheckCircleOutlined className='file-loader-hatch-status-icon loaded-icon' />;
                    } else if (
                        fileRecordStatus === statuses.UPLOADED ||
                        fileRecordStatus === statuses.SCHEMA_VALIDATED ||
                        fileRecordStatus === statuses.SHARED_STAGING ||
                        fileRecordStatus === statuses.SHARED_STAGED ||
                        fileRecordStatus === statuses.LOADER_STAGING ||
                        fileRecordStatus === statuses.LOADER_STAGED ||
                        fileRecordStatus === statuses.LOADING ||
                        fileRecordStatus === statuses.LOAD_SKIPPED
                    ) {
                        return <UpCircleOutlined className='file-loader-hatch-status-icon validated-icon' />;
                    } else if ( fileRecordStatus === statuses.FAILED || _errors.length > 0 ) {
                        return (
                            <ErrorsTooltip errors={ _errors }>
                                <CloseCircleOutlined className='file-loader-hatch-status-icon failed-icon' />
                            </ErrorsTooltip>
                        );
                    } else {
                        return <QuestionCircleOutlined className='file-loader-hatch-status-icon unknown-icon' />;
                    }
                },
                order: 0,
                width: 100,
            }
        };

        let getColumnOrderFn = getColumnOrder;
        if ( typeof getColumnOrderFn !== 'function' ) {
            let order = 1;
            getColumnOrderFn = () => {
                const result = order;
                order += 1;
                return result;
            };
        }

        let shouldShowColumnFn = shouldShowColumn;
        if ( typeof shouldShowColumnFn !== 'function' ) {
            shouldShowColumnFn = () => true;
        }

        let getColumnTitleFn = getColumnTitle;
        if ( typeof getColumnTitleFn !== 'function' ) {
            getColumnTitleFn = ( name ) => startCase( name );
        }

        let renderValueFn = renderValue;
        if ( typeof renderValueFn !== 'function' ) {
            renderValueFn = ( column, value ) => <span>{ value }</span>;
        }

        let getColumnTypeFn = getColumnType;
        if ( typeof getColumnTypeFn !== 'function' ) {
            getColumnTypeFn = () => 'string';
        }

        records.forEach( record => {
            record.fileRecordColumns.forEach( column => {
                const columnName = column.standardColumnName || column.initialFileColumnName;
                if ( shouldShowColumnFn( columnName ) ) {
                    columnMap[ columnName ] = {
                        title: getColumnTitleFn( columnName ),
                        dataIndex: columnName,
                        order: getColumnOrderFn( columnName ),
                        width: 200,
                        render: ( x ) => renderValueFn( columnName, x ? x.value : '' ),
                        sorter: true,
                        sortOrder: sorter?.field === columnName ? sorter.order : null,
                        type: getColumnTypeFn( columnName ),
                        ...getColumnFilterProps( columnName, showDropDown )
                    };
                }
            } );
        } );

        if ( Array.isArray( additionalColumns ) ) {
            additionalColumns.forEach( column => {
                if ( shouldShowColumnFn( column.dataIndex ) ) {
                    if ( typeof column.render === 'function' ) {
                        columnMap[ column.dataIndex ] = {
                            ...column,
                            order: column.order || getColumnOrderFn( column.dataIndex ),
                            type: column.type || getColumnTypeFn( column.dataIndex )
                        };
                    } else {
                        columnMap[ column.dataIndex ] = {
                            ...column,
                            order: column.order || getColumnOrderFn( column.dataIndex ),
                            width: 200,
                            render: ( x ) => renderValueFn( column.dataIndex, x ? x.value : '' ),
                            sorter: file.migrated ? true : ( a, b ) => {
                                const selectedColumn = column.dataIndex;
                                if ( selectedColumn ) {
                                    a = a[ selectedColumn ];
                                    b = b[ selectedColumn ];
                                }
                                if ( a?.value === b?.value ) {
                                    return a?.value.localeCompare(
                                        b?.value, undefined, { sensitivity: 'base' }
                                    );
                                }
                                return a?.value.localeCompare(
                                    b?.value, undefined, { sensitivity: 'base' }
                                );
                            },
                            type: column.type || getColumnTypeFn( column.dataIndex ),
                            sortOrder: sorter?.field === column.dataIndex ? sorter.order : null,
                            ...getColumnFilterProps( column.dataIndex, showDropDown )
                        };
                    }
                }
            } );
        }
        return Object.values( columnMap ).sort( ( a, b ) => a.order - b.order );

    }, [ file, records, sorter, showDropDown, additionalColumns, getColumnOrder, shouldShowColumn, getColumnTitle, renderValue, getColumnType, filterText ] );

    const data = useMemo( () => {
        if ( !records || records.length === 0 ) {
            return [];
        }
        return records.map( r => {
            const record = {
                id: r.id,
                fileRecordStatus: r.status,
                _errors: [ ...( file?.fileErrors || [] ), ...r.fileRecordErrors ]
            };

            r.fileRecordColumns.forEach( column => {
                record[ column.standardColumnName || column.initialFileColumnName ] = {
                    _errors: column.fileRecordColumnErrors,
                    value: ( column.standardColumnName === 'awardDate' || column.standardColumnName === 'Award Date' )
                        ? formatDateShort( column.value )
                        : column.value
                };
                column.fileRecordColumnErrors.forEach( error => {
                    record._errors.push( error );
                } );
            } );

            r.fileRecordData.forEach( data => {
                record[ data.key ] = {
                    _errors: [],
                    value: data.value
                };
            } );

            return record;
        } );
    }, [ records, file ] );

    return (
        <div className={ classNames( 'file-loader-file-record-table', className ) }>
            <Table
                dataSource={ data }
                columns={ columns }
                loading={ loading }
                pagination={ {
                    position: [ 'none', 'bottomRight' ],
                    pageSize,
                    showSizeChanger: false,
                    current: page,
                    total: file?.totalFileRecordCount || 0
                } }
                locale={ {
                    emptyText: 'No Records'
                } }
                scroll={ { x: 'max-content', y: false } }
                tableLayout="fixed"
                onChange={ handleTableChange }
                rowKey={ record => record.id }
            />
        </div>
    );
};

export default FileRecordTable;